import React, { useState, useEffect } from "react";
import { Snackbar, Alert, Button } from "@mui/material";

function UpdateNotification() {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(
    null,
  );

  useEffect(() => {
    // Check if service workers are supported
    if ("serviceWorker" in navigator) {
      // Listen for service worker updates
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;

          if (newWorker) {
            newWorker.addEventListener("statechange", () => {
              if (
                newWorker.state === "installed" &&
                navigator.serviceWorker.controller
              ) {
                // New service worker is installed, but old one is still controlling the page
                setWaitingWorker(newWorker);
                setShowUpdatePrompt(true);
              }
            });
          }
        });

        // Check if there's already a waiting service worker
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setShowUpdatePrompt(true);
        }
      });

      // Listen for controller change (when new service worker takes over)
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        // Force a hard reload to bypass cache
        window.location.reload();
      });
    }
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      // Tell the waiting service worker to skip waiting and become active
      waitingWorker.postMessage({ type: "SKIP_WAITING" });
      setShowUpdatePrompt(false);

      // Also clear caches and do hard reload as backup
      if ("caches" in window) {
        caches
          .keys()
          .then((names) => {
            return Promise.all(names.map((name) => caches.delete(name)));
          })
          .then(() => {
            // Hard reload after clearing cache
            (window as Window).location.reload();
          })
          .catch(() => {
            // Fallback reload if cache clearing fails
            (window as Window).location.reload();
          });
      } else {
        // If caches API not available, just do hard reload
        (window as Window).location.reload();
      }
    }
  };

  const handleDismiss = () => {
    setShowUpdatePrompt(false);
    // Show again after 5 minutes
    setTimeout(
      () => {
        if (waitingWorker) {
          setShowUpdatePrompt(true);
        }
      },
      5 * 60 * 1000,
    );
  };

  return (
    <Snackbar
      open={showUpdatePrompt}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      sx={{ top: { xs: 70, sm: 80 } }}
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <>
            <Button color="inherit" size="small" onClick={handleUpdate}>
              Update Now
            </Button>
            <Button color="inherit" size="small" onClick={handleDismiss}>
              Later
            </Button>
          </>
        }
      >
        A new version is available! Click "Update Now" to refresh.
      </Alert>
    </Snackbar>
  );
}

export default UpdateNotification;
